import type { CSSProperties } from 'react'

import { useId } from 'react'
import { Lightbulb } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    areaPath,
    smoothPath,
    svgId,
    toPoints
} from '@/lib/chart'

const ACTUAL = [38, 41, 40, 44, 47, 46, 51, 53, 52]
const FORECAST = [52, 54.5, 56.8, 59.5]
const WIDTH = 300
const HEIGHT = 150
const TOTAL = ACTUAL.length + FORECAST.length - 1
const SCALE = { width: WIDTH, height: HEIGHT, padTop: 26, padBottom: 10, min: 32, max: 62, total: TOTAL }

const actualPoints = toPoints(ACTUAL, SCALE)
const forecastPoints = toPoints(FORECAST, { ...SCALE, offset: ACTUAL.length - 1 })
const endPoint = forecastPoints[forecastPoints.length - 1]

interface ForecastPreviewProps {
    active: boolean
}

/** Lime area chart that draws itself, with a forecast callout and a tip. */
export const ForecastPreview = ({ active }: ForecastPreviewProps) => {
    const gradientId = `forecast-${svgId(useId())}`

    return (
        <div aria-hidden='true'>
            <div className='glass-dark rounded-[22px] p-4'>
                <div className='flex items-center justify-between text-[11px]'>
                    <span className='font-medium uppercase tracking-[0.2em] text-cream/55'>
                        Savings outlook
                    </span>

                    <span className='text-cream/40'>
                        Coming 90 days
                    </span>
                </div>

                <div className='relative mt-4 aspect-[2/1]'>
                    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='absolute inset-0 h-full w-full overflow-visible'>
                        <defs>
                            <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor='#e2f37a' stopOpacity='0.38' />
                                <stop offset='100%' stopColor='#e2f37a' stopOpacity='0' />
                            </linearGradient>
                        </defs>

                        {[0.25, 0.5, 0.75].map(line => (
                            <line key={line} x1='0' x2={WIDTH} y1={HEIGHT * line} y2={HEIGHT * line} stroke='rgba(255,255,255,0.08)' strokeDasharray='3 5' />
                        ))}

                        <path
                            d={areaPath([...actualPoints, ...forecastPoints.slice(1)], HEIGHT)}
                            fill={`url(#${gradientId})`}
                            className='transition-opacity duration-1000 ease-out'
                            style={{ opacity: active ? 1 : 0, transitionDelay: active ? '1300ms' : '0ms' }}
                        />

                        <path
                            d={smoothPath(actualPoints)}
                            fill='none'
                            stroke='#e2f37a'
                            strokeWidth='2.2'
                            strokeLinecap='round'
                            pathLength={1}
                            strokeDasharray='1'
                            style={{
                                strokeDashoffset: active ? 0 : 1,
                                transition: 'stroke-dashoffset 2s cubic-bezier(0.22, 1, 0.36, 1) 300ms'
                            }}
                        />

                        <path
                            d={smoothPath(forecastPoints)}
                            fill='none'
                            stroke='#e2f37a'
                            strokeOpacity='0.75'
                            strokeWidth='2'
                            strokeDasharray='4 5'
                            strokeLinecap='round'
                            className='transition-opacity duration-700'
                            style={{ opacity: active ? 1 : 0, transitionDelay: active ? '1900ms' : '0ms' }}
                        />

                        <circle
                            cx={endPoint.x}
                            cy={endPoint.y}
                            r='4'
                            fill='#e2f37a'
                            className='transition-opacity duration-500'
                            style={{ opacity: active ? 1 : 0, transitionDelay: active ? '2300ms' : '0ms', filter: 'drop-shadow(0 0 6px rgba(226,243,122,0.9))' }}
                        />
                    </svg>

                    <div
                        className={cn(
                            'glass absolute rounded-2xl px-3 py-2 text-right transition-all duration-700 ease-cinema',
                            active ? 'opacity-100' : 'opacity-0'
                        )}
                        style={{
                            right: `${100 - (endPoint.x / WIDTH) * 100}%`,
                            bottom: `${100 - (endPoint.y / HEIGHT) * 100 + 8}%`,
                            transitionDelay: active ? '2400ms' : '0ms',
                            transform: active ? 'translateY(0)' : 'translateY(8px)'
                        } as CSSProperties}
                    >
                        <span className='block text-2xl font-medium leading-none tracking-[-0.04em] text-citron'>
                            3%
                        </span>

                        <span className='mt-1 block text-[10px] uppercase tracking-[0.16em] text-cream/60'>
                            likely growth
                        </span>
                    </div>
                </div>

                <div className='mt-2 flex justify-between text-[10px] text-cream/35'>
                    <span>
                        90 days back
                    </span>

                    <span>
                        Now
                    </span>

                    <span>
                        90 days out
                    </span>
                </div>
            </div>

            <div
                className={cn(
                    'glass mt-3 inline-flex items-start gap-2 rounded-2xl px-3.5 py-2.5 text-xs leading-snug text-cream/80 transition-all duration-700 ease-cinema',
                    active ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
                )}
                style={{ transitionDelay: active ? '2700ms' : '0ms' }}
            >
                <Lightbulb className='mt-px size-3.5 shrink-0 text-citron' />
                Tip: tuck $120 into savings before the 28th for an easy month.
            </div>
        </div>
    )
}
